import { useEffect, ReactNode } from 'react';
import { useApolloClient, useSubscription } from '@apollo/client/react';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Stack, { StackProps } from '@mui/material/Stack';
import { useTranslation } from 'react-i18next';
import { mdiCheck, mdiClose } from '@mdi/js';

import { Icon } from 'common/components/Icon';
import { ITaskStateEnum, TaskStatusDocument } from 'graphql/types';
import { Button } from 'common/components/Button';
import { parseJSON } from 'common/utils/json';
import { CircularProgress } from 'common/components/CircularProgress';

interface ITranslations {
    title: string;
    progressMessage: string;
    progressFinishingMessage: string;
    failedMessage: string;
    finishedMessage: string;
    retryButton: string;
}

interface ICourseAITaskStatusBaseProps<TResultData> extends StackProps {
    taskId: string;
    onRetry?: () => void;
    translations: ITranslations;
    renderItems?: (resultData: TResultData | undefined) => ReactNode;
    isAllItemsComplete?: (resultData: TResultData | undefined) => boolean;
    renderSuccessAction?: (resultData: TResultData | undefined) => ReactNode;
    shouldShowItems?: (resultData: TResultData | undefined) => boolean;
}

export const CourseAITaskStatusBase = <TResultData,>({
    taskId,
    onRetry,
    translations,
    renderItems,
    isAllItemsComplete,
    renderSuccessAction,
    shouldShowItems,
    ...other
}: ICourseAITaskStatusBaseProps<TResultData>) => {
    const [translate] = useTranslation();
    const client = useApolloClient();

    const { data: taskStatusData } = useSubscription(TaskStatusDocument, {
        variables: { id: taskId! },
        skip: !taskId,
    });

    const { result, state } = taskStatusData?.taskStatus || {};
    const resultData: TResultData | undefined = parseJSON(result);

    const isPending = state === ITaskStateEnum.Pending;
    const isStarted = state === ITaskStateEnum.Started;
    const isSuccess = state === ITaskStateEnum.Success;
    const isFailed = state === ITaskStateEnum.Failure;

    // When the task is successful, evict the courses field from the cache.
    useEffect(() => {
        if (isSuccess) {
            client.cache.evict({ fieldName: 'courses' });
        }
    }, [isSuccess, client.cache]);

    const allItemsComplete = isAllItemsComplete?.(resultData) ?? false;
    const showItems = shouldShowItems?.(resultData) ?? true;

    return (
        <Stack spacing={2} {...other}>
            <Typography variant="h3">
                {translate(translations.title)}
            </Typography>

            {!isFailed && showItems && renderItems && (
                <Box>
                    <Divider sx={{ mb: 2 }} />
                    {renderItems(resultData)}
                </Box>
            )}

            {(isStarted || isPending) && (
                <Stack spacing={2}>
                    <Divider />
                    <Stack alignItems="center" direction="row" spacing={1}>
                        <CircularProgress size={19} />
                        <Typography variant="body2">
                            {allItemsComplete
                                ? translate(
                                      translations.progressFinishingMessage
                                  )
                                : translate(translations.progressMessage)}
                        </Typography>
                    </Stack>
                    <Divider />
                </Stack>
            )}

            {isFailed && (
                <Stack spacing={2}>
                    <Divider />

                    <Stack alignItems="center" direction="row" spacing={1}>
                        <Icon
                            path={mdiClose}
                            size="1.9rem"
                            style={{
                                color: 'var(--mui-palette-error-main)',
                            }}
                        />
                        <Typography variant="body2">
                            {translate(translations.failedMessage)}
                        </Typography>
                    </Stack>

                    <Divider />

                    <Button
                        color="primary"
                        sx={{
                            width: '100%',
                        }}
                        variant="contained"
                        onClick={onRetry}
                    >
                        {translate(translations.retryButton)}
                    </Button>
                </Stack>
            )}

            {isSuccess && (
                <Stack spacing={2}>
                    <Divider />

                    <Stack alignItems="center" direction="row" spacing={1}>
                        <Icon
                            path={mdiCheck}
                            size="1.9rem"
                            style={{
                                color: 'var(--mui-palette-success-main)',
                            }}
                        />
                        <Typography variant="body2">
                            {translate(translations.finishedMessage)}
                        </Typography>
                    </Stack>

                    <Divider />

                    {renderSuccessAction?.(resultData)}
                </Stack>
            )}
        </Stack>
    );
};
